<template>
  <div class="widget-job-alert_page__wrapper">
    <router-link class="back-button" to="/">
      <arrow-left-icon size="1.5x" class="mr-2 back-button__icon"></arrow-left-icon>
      {{ this.$t('jobDetails.backToJobs') }}
    </router-link>
    <div class="widget-job-alert_page__header">
      <p class="text-3xl font-semibold">{{ $t('common.createJobAlert') }}</p>
      <p class="text-lg">{{ $t('common.changeJobAlert') }}</p>
    </div>
    <div class="flex flex-row justify-center md:justify-around md:min-w-7xl w-full mt-8 widget-job-alert_page__content">
      <div v-if="loading" class="flex items-center justify-center w-full h-auto widget-job-alert_page__loader">
        <div class="loader"></div>
      </div>
      <div v-show="!loading" class="flex flex-col bg-white w-full md:mr-6 px-6 py-8 widget-job-alert_page-card__wrapper">
        <el-form :model="form" class="widget-job-alert_page-card" ref="form" label-position="top" :rules="validationRules">
          <div class="flex flex-col widget-job-alert_page-switch">
            <span class="uppercase text-sm text-shuttlegray">{{ $t('common.active') }}</span>
            <el-switch v-model="active" :active-color="'var(--color-primary)'" class="mt-2"/>
          </div>
          <base-input class="mt-8 widget-job-alert_page-email" :label="this.$t('common.email')" :readonly="editMode" prop="email" v-model="form.email"/>
          <base-input class="mt-8 widget-job-alert_page-search" :label="this.$t('common.search')" :info-text="$t('jobAlert.infoText')"
                      v-model="query.search"/>
          <div class="flex flex-col widget-job-alert_page-city">
            <span class="uppercase text-sm text-shuttlegray">{{ $t('common.city') }}</span>
            <el-autocomplete v-model="query.location"
                             class="widget-autocomplete__component"
                             :placeholder="$t('components.input.location')"
                             value-key="label"
                             @select="onCityChange"
                             :fetch-suggestions="querySearchLocation"
                             :trigger-on-focus="false"/>
          </div>
          <div class="self-start mt-8 w-full widget-job-alert_page-range">
            <span class="uppercase text-sm text-shuttlegray">{{ $t('common.range') }}</span>
            <el-select v-model="query.range" class="w-full">
              <el-option v-for="(item, key) in RANGE_OPTIONS"
                         :key="key"
                         :label="item.label"
                         :value="item.value"/>
            </el-select>
          </div>
          <div class="self-start mt-8 w-full widget-job-alert_page-frequency">
            <span class="uppercase text-sm text-shuttlegray">{{ $t('common.frequency') }}</span>
            <el-select v-model="frequency" class="w-full">
              <el-option v-for="(item, key) in ALERT_FREQUENCIES"
                         :key="key"
                         :label="$t(item.label)"
                         :value="item.value"/>
            </el-select>
          </div>
          <div v-if="!editMode" class="flex justify-center mt-8 widget-job-alert_page-buttons__wrapper">
            <div v-if="loadingButtons" class="flex items-center justify-center w-full h-auto widget-job-alert_page-buttons__loader">
              <div class="loader"></div>
            </div>
            <el-button v-else
                       class="widget-job-alert_page-buttons__create"
                       type="secondary"
                       @click="createAlert">
              <bell-icon class="mr-2"/>
              <span>{{ $t('common.createAlert') }}</span>
            </el-button>
          </div>
          <div v-else class="mt-8 widget-job-alert_page-buttons_edit-mode__wrapper">
            <div v-if="loadingButtons" class="flex items-center justify-center w-full h-auto widget-job-alert_page-buttons_edit-mode_loader">
              <div class="loader"></div>
            </div>
            <div v-else class="flex flex-col justify-center sm:flex-row widget-job-alert_page-buttons_edit-mode__content">
              <div class="flex justify-center mb-4 sm:mb-0 sm:mr-4 widget-job-alert_page-buttons_edit-mode__delete">
                <el-button type="secondary"
                           class="bg-danger"
                           @click="deleteAlert">
                  <x-icon class="mr-2"/>
                  <span>{{ $t('common.deleteAlert') }}</span>
                </el-button>
              </div>
              <div class="flex justify-center widget-job-alert_page-buttons_edit-mode__save">
                <el-button type="secondary"
                           @click="saveAlert">
                  <save-icon class="mr-2"/>
                  <span>{{ $t('common.saveAlert') }}</span>
                </el-button>
              </div>
            </div>
          </div>

        </el-form>
      </div>
      <div v-if="!loading" class="mr-3 md:w-2/6 widget-job-alert_page_filters__wrapper">
        <JobListFilters :jobSegments="segment_ids" :showAvailableSegments="false" :showBadgeCount="true"
                        @change="onSegmentsChange"/>
      </div>
    </div>
  </div>
</template>
<script>
import get from 'lodash/get';
import { ArrowLeftIcon, BellIcon, SaveIcon, XIcon } from 'vue-feather-icons'
import { ALERT_FREQUENCIES, RANGE_OPTIONS } from '../enum/rangeOptions'
import { Notification, Switch } from 'element-ui'
import { getAlert, createAlert, updateAlert, deleteAlert } from '../API/alert'
import BaseInput from '../components/inputs/BaseInput'
import JobListFilters from '../components/filters/JobListFilters'
import { getCountries } from '../API/autocomplete'

export default {
  name: 'job-alerts',
  components: {
    ArrowLeftIcon,
    BellIcon,
    BaseInput,
    JobListFilters,
    SaveIcon,
    XIcon,
    'el-switch': Switch,
  },
  data() {
    return {
      form: {
        email: null
      },
      query: {
        search: '',
        range: '',
        location: '',
        lat: '',
        lon: ''
      },
      active: true,
      editMode: false,
      loading: false,
      loadingButtons: false,
      frequency: 'weekly',
      segment_ids: [],
      ALERT_FREQUENCIES,
      RANGE_OPTIONS,
    }
  },
  watch: {
    '$route.query.uuid': {
      immediate: true,
      async handler(val) {
        if (!val) {
          return
        }

        this.editMode = true
        this.loading = true
        const res = await getAlert(val)

        if (Object.keys(res).length > 0) {
          this.form.email = res.email
          this.active = res.active
          this.query.search = res.query.search
          this.query.range = res.query.range
          this.frequency = res.frequency
          if (get(res, 'query.segment_ids', []).length > 0) {
            this.segment_ids = res.query.segment_ids[0]
          }
          this.query.location = res.query.location
          this.query.lat = res.query.lat
          this.query.lon = res.query.lon
          this.loading = false
        } else {
          this.onCreateMode()
          this.loading = false
        }
      }
    }
  },
  computed: {
    validationRules() {
      return {
        email: [
          { required: true, message: this.$t('jobApply.emailRequired'), trigger: 'blur' },
          { type: 'email', message: this.$t('jobApply.validEmail'), trigger: 'blur' }
        ]
      }
    }
  },
  methods: {
    onCreateMode() {
      this.$router.replace({
        query: null
      })
      this.editMode = false
      this.form.email = null
      this.query = {}
      this.active = true
      this.frequency = 'weekly'
      this.segment_ids = []
      this.query.location = ''
    },
    onSegmentsChange(newSegments) {
      this.segment_ids = newSegments
    },
    async querySearchLocation(queryString, cb) {
      let countries;
      let autocompleteCountries = this.$store.getters['countries']
      autocompleteCountries = autocompleteCountries.map(c => c.code).toString()

      if (queryString.length > 1) {
        countries = await getCountries(queryString, autocompleteCountries)
      } else {
        cb([]);
        return
      }
      cb(countries);
    },
    onCityChange(place) {
      this.query.location = place.label
      this.query.lat = place.lat
      this.query.lon = place.lon
    },
    formatModelData() {
      const segment_ids = this.groupSegments()
      const frequency = this.frequency

      return {
        email: this.form.email,
        query: { ...this.query, segment_ids },
        frequency
      }
    },
    async createAlert() {
      try {
        await this.$refs.form.validate()
        this.loadingButtons = true

        const data = this.formatModelData()
        const res = await createAlert(data)

        if (res.uuid) {
          this.$router.push(`?uuid=${res.uuid}`)
        }

        Notification.success({ message: this.$t('common.jobAlertCreated') })
        this.loadingButtons = false
      } catch (e) {
        console.log(e)
      }
    },
    async saveAlert() {
      try {
        this.loadingButtons = true
        const data = this.formatModelData()
        data.active = this.active
        const res = await updateAlert(this.$route.query.uuid, data)

        if (res) {
          Notification.success({
            message: this.$t('jobAlert.alertUpdated')
          })
        } else {
          Notification.error({
            message: this.$t('common.somethingWrong')
          })
        }
        this.loadingButtons = false
      } catch (e) {
        console.log(e)
      }
    },
    async deleteAlert() {
      try {
        this.loadingButtons = true
        const res = await deleteAlert(this.$route.query.uuid)

        if (res) {
          Notification.success({
            message: this.$t('jobAlert.alertDeleted')
          })
        } else {
          Notification.error({
            message: 'Something went wrong'
          })
        }

        this.loadingButtons = false
        this.onCreateMode()
      } catch (e) {
        console.log(e)
      }
    },
    groupSegments() {
      const segmentsIdsMap = new Map()
      this.segment_ids.forEach(sId => {
        const groupId = this.$store.getters['getSegmentsById'].segment_group_id
        const groupIds = segmentsIdsMap.get(groupId)

        if (groupIds) {
          groupIds.push(sId)
          segmentsIdsMap.set(groupId, groupIds)
        } else {
          segmentsIdsMap.set(groupId, [sId])
        }
      })

      return Array.from(segmentsIdsMap.values())
    },
  }
}
</script>
<style lang="scss" scoped>
.pac-target-input {
  @apply border-b border-gray pb-2 px-3 text-sm text-shuttlegray;
}
</style>
